import React from 'react';
import { Form, Dropdown } from 'semantic-ui-react';
import styled, { css } from 'styled-components';
import {
  faUser,
  faRobot,
  faWandMagicSparkles,
  faCircleQuestion,
} from '@fortawesome/free-solid-svg-icons';
import { getTheme } from '../../constants/theme';
import {
  manuscriptLabel,
  feedbackLabel,
  latestDisclosure,
} from './aiDisclosure';

// Follow the application's bright/dark palette, including isolated feedback views.
const palette = ({ theme }) =>
  theme.navigation || getTheme('bright').navigation;

const DisclosurePill = styled.span`
  display: inline-block;
  max-width: 100%;
  box-sizing: border-box;
  padding: 3px 10px;
  border: 1px ${({ $undeclared }) => ($undeclared ? 'dashed' : 'solid')}
    ${props => palette(props).border};
  border-radius: 999px;
  background: ${props =>
    props.$generated
      ? palette(props).activeBackground
      : palette(props).surface};
  color: ${props =>
    props.$generated ? palette(props).activeText : palette(props).mutedText};
  font-family: inherit;
  font-size: 0.857rem;
  font-weight: 600;
  line-height: 1.5;
  vertical-align: middle;
  white-space: normal;
  overflow-wrap: anywhere;
`;

const DisclosureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const DisclosureDetails = styled.div`
  margin-top: 8px;
`;

export function ManuscriptDisclosure({ book, details = false }) {
  const value = latestDisclosure(book);
  return (
    <div style={{ margin: '8px 0' }}>
      <DisclosureRow>
        <DisclosurePill
          $generated={value?.type === 'generated'}
          $undeclared={
            !['human', 'assisted', 'generated'].includes(value?.type)
          }
        >
          {manuscriptLabel(value?.type)}
          {value?.type === 'generated' && (
            <>
              {' '}
              · {value.extent === 'most' ? 'Most or all text' : 'Some passages'}
            </>
          )}
        </DisclosurePill>
        {value?.cover === 'generated' && (
          <DisclosurePill $generated>AI-generated cover</DisclosurePill>
        )}
        {value?.translation === 'generated' && (
          <DisclosurePill $generated>AI translation</DisclosurePill>
        )}
      </DisclosureRow>
      {details && (
        <DisclosureDetails>
          <p>{value?.note}</p>
          <small>
            Declared by the author; not independently verified.{' '}
            <a
              href="https://betareader.io/community-guidelines"
              target="_blank"
              rel="noopener noreferrer"
            >
              AI disclosure rules
            </a>
          </small>
          <p>
            Reader feedback must be the reader's own, not written or generated
            by AI. Readers may not send manuscript text to AI services.
          </p>
        </DisclosureDetails>
      )}
    </div>
  );
}

export function FeedbackBadge({ origin }) {
  return (
    <DisclosurePill
      $generated={origin === 'generated'}
      $undeclared={!['personal', 'assisted', 'generated'].includes(origin)}
    >
      {feedbackLabel(origin)}
    </DisclosurePill>
  );
}

const feedbackIcon = origin =>
  ({ personal: faUser, generated: faRobot, assisted: faWandMagicSparkles })[
    origin
  ] || faCircleQuestion;

function FeedbackSourceIcon({ origin }) {
  const [width, height, , , path] = feedbackIcon(origin).icon;
  return (
    <svg
      width="14"
      height="14"
      viewBox={`0 0 ${width} ${height}`}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d={path} />
    </svg>
  );
}

// The whole pill opens the actions; keep the provenance and its menu together.
const PillDropdown = styled(Dropdown)`
  && {
    max-width: 100%;
    margin-left: auto;
  }
  &&:focus > span,
  &&.active > span {
    outline: 2px solid ${props => palette(props).accent};
    outline-offset: 2px;
  }
  && > span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  && .menu {
    max-width: calc(100vw - 32px);
  }
  && .feedback-source-icon,
  && .menu .feedback-source-summary {
    display: none;
  }
  ${({ $compact }) =>
    $compact &&
    css`
      && > span {
        min-height: 32px;
        min-width: 56px;
        justify-content: center;
      }
      && .feedback-source-label {
        display: none;
      }
      && .feedback-source-icon {
        display: inline-flex;
        width: 14px;
        justify-content: center;
      }
      && .menu .feedback-source-summary {
        display: block;
        white-space: normal;
        text-transform: none;
        letter-spacing: normal;
      }
    `}
`;

export function FeedbackMenu({
  origin,
  label,
  compact = false,
  direction = 'left',
  children,
}) {
  return (
    <PillDropdown
      $compact={compact}
      aria-label={`${label}: ${feedbackLabel(origin)}`}
      title={feedbackLabel(origin)}
      icon={null}
      direction={direction}
      trigger={
        <DisclosurePill
          $generated={origin === 'generated'}
          $undeclared={!['personal', 'assisted', 'generated'].includes(origin)}
        >
          <span className="feedback-source-icon" aria-hidden="true">
            <FeedbackSourceIcon origin={origin} />
          </span>
          <span className="feedback-source-label">{feedbackLabel(origin)}</span>
          <span aria-hidden="true" style={{ fontSize: '16px', lineHeight: 1 }}>
            ⋯
          </span>
        </DisclosurePill>
      }
    >
      <Dropdown.Menu className="br-text">
        <Dropdown.Header
          className="feedback-source-summary"
          content={feedbackLabel(origin)}
        />
        {children}
      </Dropdown.Menu>
    </PillDropdown>
  );
}

const ConfirmationBox = styled.div`
  margin: 12px 0;
  padding: 14px 16px;
  border: 1px solid ${props => palette(props).border};
  border-radius: 12px;
  background: ${props => palette(props).surface};
  color: ${props => palette(props).text};
  .ui.checkbox label {
    color: inherit !important;
    line-height: 1.5;
  }
  .field {
    margin-bottom: 8px;
  }
  small {
    display: block;
    line-height: 1.5;
  }
  p {
    margin: 8px 0 0;
    font-size: 0.85em;
  }
`;

export function FeedbackOrigin({ value, onChange }) {
  return (
    <ConfirmationBox>
      <Form.Checkbox
        aria-label="I confirm this is my own feedback, based on my reading, and I did not use AI to process the manuscript or write this feedback."
        checked={value === 'personal'}
        label="I confirm this is my own feedback, based on my reading, and I did not use AI to process the manuscript or write this feedback."
        onChange={(_, { checked }) =>
          onChange(checked ? 'personal' : 'undeclared')
        }
      />
      <small>
        Write your own observations in your own words. Do not use AI to analyze
        the manuscript or draft or rewrite your feedback.
      </small>
      <p>Do not send manuscript text to AI services.</p>
    </ConfirmationBox>
  );
}

export function ReportAiDisclosure({ bookId, feedbackId, menuItem = false }) {
  const subject = encodeURIComponent('Report misleading AI disclosure');
  const body = encodeURIComponent(
    `Book: https://app.betareader.io/books/${bookId}\n${feedbackId ? `Feedback: ${feedbackId}\n` : ''}Please describe the disclosure concern and supporting context:\n`
  );
  const item = (
    <Dropdown.Item
      as="a"
      href={`mailto:hello@betareader.io?subject=${subject}&body=${body}`}
      style={{ fontSize: '0.85rem', whiteSpace: 'normal', maxWidth: 240 }}
      text="Report misleading AI disclosure"
    />
  );
  if (menuItem) return item;
  return (
    <Dropdown
      aria-label={feedbackId ? 'Feedback actions' : 'Manuscript actions'}
      icon={{ name: 'ellipsis horizontal', color: 'grey' }}
      direction="left"
      style={{ float: 'right', padding: '4px 6px' }}
    >
      <Dropdown.Menu className="br-text">{item}</Dropdown.Menu>
    </Dropdown>
  );
}
